import { useSyncExternalStore } from "react";

/**
 * Até quando cada conversa já foi vista — para marcar as que têm mensagem nova.
 *
 * Guardado NESTE navegador (localStorage), por clínica: é o computador da
 * recepção que precisa saber o que ela ainda não leu. Não vale entre aparelhos
 * — ler no celular não apaga o "nova" do computador. Para isso seria preciso
 * gravar no banco por pessoa; fica para quando alguém sentir falta.
 *
 * Na primeira vez numa clínica, tudo que já existe conta como lido: sem isso
 * a lista inteira acenderia de uma vez.
 */
type Registro = { inicio: string; vistas: Record<string, string> };

const chave = (clinica: string) => `lummichat_lidas_${clinica}`;
const ouvintes = new Set<() => void>();
let versao = 0;
const memoria = new Map<string, Registro>();

function ler(clinica: string): Registro {
  const ja = memoria.get(clinica);
  if (ja) return ja;
  let reg: Registro | null = null;
  try {
    const bruto = localStorage.getItem(chave(clinica));
    if (bruto) reg = JSON.parse(bruto) as Registro;
  } catch {}
  if (!reg || typeof reg.inicio !== "string" || typeof reg.vistas !== "object") {
    reg = { inicio: new Date().toISOString(), vistas: {} };
    gravar(clinica, reg);
  }
  memoria.set(clinica, reg);
  return reg;
}

function gravar(clinica: string, reg: Registro) {
  memoria.set(clinica, reg);
  try {
    localStorage.setItem(chave(clinica), JSON.stringify(reg));
  } catch {}
}

/** Marca a conversa como vista até `ateISO` (a última mensagem dela). */
export function marcarVista(clinica: string, telefone: string, ateISO: string) {
  const reg = ler(clinica);
  const antes = reg.vistas[telefone];
  if (antes && antes >= ateISO) return;
  gravar(clinica, { ...reg, vistas: { ...reg.vistas, [telefone]: ateISO } });
  versao++;
  for (const f of ouvintes) f();
}

function assinar(f: () => void) {
  ouvintes.add(f);
  return () => {
    ouvintes.delete(f);
  };
}

/**
 * Devolve "tem mensagem nova do paciente nesta conversa?" — o paciente escreveu
 * depois da última vez que alguém viu a conversa neste navegador.
 */
export function useNaoLida(clinica: string): (telefone: string, ultimaDoClienteISO: string | null) => boolean {
  useSyncExternalStore(assinar, () => versao, () => versao);
  const reg = ler(clinica);
  return (telefone, ultimaDoClienteISO) => {
    if (!ultimaDoClienteISO) return false;
    const vista = reg.vistas[telefone] ?? reg.inicio;
    return Date.parse(ultimaDoClienteISO) > Date.parse(vista);
  };
}
